import React, { useMemo, useRef } from 'react';
import { Field } from 'react-final-form';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isOriginInUse } from '../../../util/search';
import { parse } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';
import { hasSameSDKBounds } from '../../../util/maps';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { LocationAutocompleteInput, RangeSlider } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import css from './LocationFilter.module.css';

const { LatLng, LatLngBounds } = sdkTypes;

// The radius goes from a nearby suburb to most of a continent. The slider moves along a
// logarithmic scale, so small distances are as easy to pick as large ones.
const RADIUS_MIN_KM = 5;
const RADIUS_MAX_KM = 10000;
const RADIUS_DEFAULT_KM = 150;
const SLIDER_STEPS = 1000;
const RADIUS_RATIO = RADIUS_MAX_KM / RADIUS_MIN_KM;

const EARTH_RADIUS_KM = 6371.0088;
const toRadians = degrees => (degrees * Math.PI) / 180;
const toDegrees = radians => (radians * 180) / Math.PI;

// A friendly distance within the allowed range: 1 km steps up to 100 km, then 10 km steps up
// to 1,000 km, then 100 km steps.
const clampRadius = km => {
  const step = km < 100 ? 1 : km < 1000 ? 10 : 100;
  const rounded = Math.round(km / step) * step;
  return Math.min(RADIUS_MAX_KM, Math.max(RADIUS_MIN_KM, rounded));
};

const sliderPositionFromRadius = km =>
  Math.round((Math.log(clampRadius(km) / RADIUS_MIN_KM) / Math.log(RADIUS_RATIO)) * SLIDER_STEPS);
const radiusFromSliderPosition = position =>
  clampRadius(RADIUS_MIN_KM * Math.pow(RADIUS_RATIO, position / SLIDER_STEPS));

/**
 * The search area around a place: a box reaching `radiusKm` out from its centre.
 *
 * This is plain math on purpose. It works the same whichever map provider the marketplace
 * uses (Mapbox or Google Maps), and doesn't need that provider's library to be loaded.
 *
 * @param {LatLng} center - The picked place
 * @param {number} radiusKm - Distance from the place, in kilometres
 * @returns {LatLngBounds}
 */
export const boundsAroundPoint = (center, radiusKm) => {
  const latDelta = toDegrees(radiusKm / EARTH_RADIUS_KM);
  const lngDelta = latDelta / Math.max(Math.cos(toRadians(center.lat)), 0.01);
  const clampLat = lat => Math.max(-90, Math.min(90, lat));

  // A wide radius can reach past the poles or all the way around the globe, but the listings
  // API only accepts real coordinates, so the box is kept within them.
  const coversAllLongitudes = lngDelta >= 180;
  const east = coversAllLongitudes ? 180 : Math.min(180, center.lng + lngDelta);
  const west = coversAllLongitudes ? -180 : Math.max(-180, center.lng - lngDelta);

  return new LatLngBounds(
    new LatLng(clampLat(center.lat + latDelta), east),
    new LatLng(clampLat(center.lat - latDelta), west)
  );
};

// The inverse of boundsAroundPoint, so the filter can be restored from the URL alone: the box is
// centred on the picked place and is two radiuses tall. (Only approximately for a radius so wide
// that the box had to be kept within the coordinates above.)
const centerOfBounds = ({ ne, sw }) => new LatLng((ne.lat + sw.lat) / 2, (ne.lng + sw.lng) / 2);
const radiusOfBounds = ({ ne, sw }) =>
  clampRadius(toRadians((ne.lat - sw.lat) / 2) * EARTH_RADIUS_KM);

const identity = v => v;

/**
 * Filter listings by distance from a location.
 *
 * Picking a place, or moving the radius slider, searches listings inside the area around the
 * place. The search is stored in the URL like the location search of the topbar: `address` and
 * `bounds`, so it works with the rest of the search page as is.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - The ID
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Object} props.location - The router location
 * @param {Object} props.history - The router history
 * @param {Array} props.routeConfiguration - The route configuration
 * @param {Object} props.config - The marketplace configuration
 * @param {Object} props.urlQueryParams - The current (valid) search query params
 * @returns {JSX.Element}
 */
const LocationFilter = props => {
  const {
    id,
    className,
    rootClassName,
    location,
    history,
    routeConfiguration,
    config,
    urlQueryParams,
    ...rest
  } = props;
  const intl = useIntl();
  const inputRef = useRef(null);

  const search = location.search;
  // Kept stable for as long as the URL doesn't change, so that re-renders don't reset what the
  // user is typing.
  const initialValues = useMemo(() => {
    const { address, bounds } = parse(search, { latlng: ['origin'], latlngBounds: ['bounds'] });
    const hasLocation = !!(address && bounds);
    return {
      location: hasLocation
        ? { search: address, selectedPlace: { address, origin: centerOfBounds(bounds), bounds } }
        : null,
      radius: [hasLocation ? radiusOfBounds(bounds) : RADIUS_DEFAULT_KM],
    };
  }, [search]);
  const hasLocation = !!initialValues.location;

  const pushSearch = searchParams => {
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleSubmit = values => {
    const { address, bounds, origin, ...otherParams } = urlQueryParams || {};

    // Cleared
    if (!values) {
      if (address || bounds) {
        pushSearch(otherParams);
      }
      return;
    }

    // Still typing: there's no place to search around yet.
    const place = values.location?.selectedPlace;
    if (!place?.origin) {
      return;
    }

    const radiusKm = clampRadius(values.radius?.[0] || RADIUS_DEFAULT_KM);
    const nextAddress = values.location.search || place.address;
    const nextBounds = boundsAroundPoint(place.origin, radiusKm);
    if (nextAddress === address && bounds && hasSameSDKBounds(nextBounds, bounds)) {
      return;
    }

    const originMaybe = isOriginInUse(config) ? { origin: place.origin } : {};
    pushSearch({ ...otherParams, address: nextAddress, bounds: nextBounds, ...originMaybe });
  };

  // Dragging the slider changes the value many times a second, so searching waits for a pause,
  // like the price filter does. Clearing searches straight away.
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const debouncedSubmit = useMemo(
    () => debounce(values => handleSubmitRef.current(values), 400),
    []
  );
  const onSubmit = values => (values ? debouncedSubmit(values) : handleSubmit(null));

  const label = intl.formatMessage({ id: 'LocationFilter.label' });
  const radiusAriaLabel = intl.formatMessage({ id: 'LocationFilter.radiusAriaLabel' });

  return (
    <FilterPlain
      className={classNames(rootClassName || css.root, className)}
      label={label}
      ariaLabel={label}
      isSelected={hasLocation}
      id={`${id}.plain`}
      liveEdit
      onClear={() => debouncedSubmit.cancel()}
      onSubmit={onSubmit}
      initialValues={initialValues}
      {...rest}
    >
      <div className={css.fields}>
        <Field
          name="location"
          format={identity}
          render={({ input, meta }) => {
            const { onChange, ...restInput } = input;
            // Like the topbar search: once a place is picked, the input is blurred (which also
            // hides the software keyboard). Otherwise the autocomplete treats the still-focused
            // input as unfocused, and typing a new place wouldn't show any suggestions.
            const handleChange = value => {
              onChange(value);
              if (value?.selectedPlace) {
                inputRef.current?.blur();
              }
            };

            return (
              <LocationAutocompleteInput
                id={`${id}.location`}
                className={css.locationRoot}
                iconClassName={css.locationIcon}
                inputClassName={css.locationInput}
                predictionsClassName={css.predictions}
                placeholder={intl.formatMessage({ id: 'LocationFilter.placeholder' })}
                ariaLabel={label}
                closeOnBlur
                inputRef={inputRef}
                input={{ ...restInput, onChange: handleChange }}
                meta={meta}
              />
            );
          }}
        />

        <Field
          name="radius"
          render={({ input }) => {
            const radiusKm = Array.isArray(input.value) ? input.value[0] : RADIUS_DEFAULT_KM;
            return (
              <div className={css.radius}>
                <p className={css.radiusLabel}>
                  <FormattedMessage
                    id="LocationFilter.radiusLabel"
                    values={{ radius: intl.formatNumber(radiusKm) }}
                  />
                </p>
                <div className={css.sliderWrapper}>
                  <RangeSlider
                    min={0}
                    max={SLIDER_STEPS}
                    step={1}
                    handles={[sliderPositionFromRadius(radiusKm)]}
                    ariaLabels={[radiusAriaLabel]}
                    onChange={handles => input.onChange([radiusFromSliderPosition(handles[0])])}
                  />
                </div>
                <div className={css.radiusRange}>
                  <FormattedMessage
                    id="LocationFilter.radiusValue"
                    values={{ radius: intl.formatNumber(RADIUS_MIN_KM) }}
                  />
                  <FormattedMessage
                    id="LocationFilter.radiusValue"
                    values={{ radius: intl.formatNumber(RADIUS_MAX_KM) }}
                  />
                </div>
              </div>
            );
          }}
        />
      </div>
    </FilterPlain>
  );
};

export default LocationFilter;
